import { createContext, useContext, useState } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Bot, Box, Building2, TriangleAlert, X, Zap, type LucideIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { store } from "../../store";
import { defaultPageSize } from "../../utils/constants";
import { Badge } from "@/components/ui/badge";
import type { RobotsFilter } from "../../domain/services/service-robots";
import type {
    ConnectionStatus,
    LifecycleState,
    RobotClass,
    RobotHealth,
    RobotQuickTag,
    RobotType,
} from "../types";

export type RobotFilterId =
    | `quick:${RobotQuickTag}`
    | `class:${RobotClass}`
    | `department:${string}`
    | `type:${RobotType}`
    | `connectivity:${ConnectionStatus}`
    | `health:${RobotHealth}`
    | `lifecycle:${LifecycleState}`;

export function robotsFilterFromSelection(selected: ReadonlySet<RobotFilterId>): RobotsFilter {
    const values = (category: string): string[] =>
        [...selected]
            .filter((id) => id.startsWith(`${category}:`))
            .map((id) => id.slice(category.length + 1));

    return {
        tags: values("quick") as RobotQuickTag[],
        classes: values("class") as RobotClass[],
        departmentIds: values("department"),
        types: values("type") as RobotType[],
        connectivity: values("connectivity") as ConnectionStatus[],
        health: values("health") as RobotHealth[],
        lifecycles: values("lifecycle") as LifecycleState[],
    };
}

const FilterSelectionContext = createContext<{
    selected: Set<RobotFilterId>;
    toggle: (id: RobotFilterId) => void;
}>({ selected: new Set(), toggle: () => {} });

const FilterSection = ({
    title,
    icon: Icon,
    children,
}: React.PropsWithChildren<{
    title: string;
    icon: LucideIcon;
}>): JSX.Element => (
    <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2 font-semibold">
            <Icon className="size-4" />
            {title}
        </div>
        <div className="flex flex-wrap gap-2">{children}</div>
    </div>
);

const FilterChip = ({
    id,
    icon: Icon,
    dotClassName,
    children,
}: React.PropsWithChildren<{
    id: RobotFilterId;
    icon?: LucideIcon;
    dotClassName?: string;
}>): JSX.Element => {
    const { selected, toggle } = useContext(FilterSelectionContext);

    return (
        <button
            type="button"
            onClick={() => toggle(id)}
            className={cn(
                "border-border flex cursor-pointer items-center gap-2 rounded-md border px-4 py-2 text-sm",
                selected.has(id) ? "bg-muted" : "hover:bg-accent",
            )}
        >
            {Icon && <Icon className="size-4" />}
            {dotClassName && <span className={cn("size-2 rounded-full", dotClassName)} />}
            {children}
        </button>
    );
};

export type RobotFilterSelectionProps = {
    selected: Set<RobotFilterId>;
    onChange: (selected: Set<RobotFilterId>) => void;
};

export type RobotFilterPopoverProps = React.PropsWithChildren<RobotFilterSelectionProps>;

export const RobotFilterPopover = observer(
    ({ children, selected, onChange }: RobotFilterPopoverProps): JSX.Element => {
        const { t } = useTranslation();
        const [open, setOpen] = useState(false);

        const departments = store.repositoryDepartments
            .byQuery({ pageSize: defaultPageSize })
            .filter((department) => department !== undefined);

        const toggle = (id: RobotFilterId): void => {
            const next = new Set(selected);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            onChange(next);
        };

        return (
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>{children}</PopoverTrigger>
                <PopoverContent
                    align="start"
                    className="flex max-h-[70vh] w-[560px] flex-col gap-6 overflow-y-auto p-6"
                >
                    <FilterSelectionContext.Provider value={{ selected, toggle }}>
                        <FilterSection title={t("page.robots.filterPopup.quickTags")} icon={Zap}>
                            <FilterChip id="quick:needs_attention" icon={TriangleAlert}>
                                {t("page.robots.filterPopup.needsAttention")}
                            </FilterChip>
                            <FilterChip id="quick:close_to_discharge">
                                {t("page.robots.filterPopup.closeToDischarge")}
                            </FilterChip>
                            <FilterChip id="quick:lost">
                                {t("page.robots.filterPopup.lost")}
                            </FilterChip>
                        </FilterSection>
                        <FilterSection title={t("page.robots.filterPopup.class")} icon={Bot}>
                            <FilterChip id="class:agv">AGV</FilterChip>
                            <FilterChip id="class:amr">AMR</FilterChip>
                        </FilterSection>
                        <FilterSection
                            title={t("page.robots.filterPopup.departments")}
                            icon={Building2}
                        >
                            {departments.map((department) => (
                                <FilterChip id={`department:${department.id}`} key={department.id}>
                                    {store.i18n.formatDepartment(department)}
                                </FilterChip>
                            ))}
                        </FilterSection>
                        <FilterSection title={t("page.robots.filterPopup.robotType")} icon={Box}>
                            <FilterChip id="type:forklift">
                                {t("page.robots.type.forklift")}
                            </FilterChip>
                            <FilterChip id="type:unit_load">
                                {t("page.robots.type.unitLoad")}
                            </FilterChip>
                            <FilterChip id="type:tugger">{t("page.robots.type.tugger")}</FilterChip>
                            <FilterChip id="type:pallet_mover">
                                {t("page.robots.type.palletMover")}
                            </FilterChip>
                            <FilterChip id="type:cart">{t("page.robots.type.cart")}</FilterChip>
                            <FilterChip id="type:cleaner">
                                {t("page.robots.type.cleaner")}
                            </FilterChip>
                        </FilterSection>
                        <FilterSection
                            title={t("page.robots.filterPopup.statusAndCondition")}
                            icon={TriangleAlert}
                        >
                            <FilterChip id="connectivity:online" dotClassName="bg-green-500">
                                {t("page.robots.connectivity.online")}
                            </FilterChip>
                            <FilterChip id="connectivity:delayed" dotClassName="bg-amber-500">
                                {t("page.robots.connectivity.delayed")}
                            </FilterChip>
                            <FilterChip id="connectivity:offline" dotClassName="bg-zinc-400">
                                {t("page.robots.connectivity.offline")}
                            </FilterChip>
                            <FilterChip id="health:healthy" dotClassName="bg-green-500">
                                {t("page.robots.health.healthy")}
                            </FilterChip>
                            <FilterChip id="health:warning" dotClassName="bg-amber-500">
                                {t("page.robots.health.warning")}
                            </FilterChip>
                            <FilterChip id="health:critical" dotClassName="bg-red-500">
                                {t("page.robots.health.critical")}
                            </FilterChip>
                            <FilterChip id="lifecycle:planned">
                                {t("page.robots.lifecycle.planned")}
                            </FilterChip>
                            <FilterChip id="lifecycle:commissioning">
                                {t("page.robots.lifecycle.commissioning")}
                            </FilterChip>
                            <FilterChip id="lifecycle:active">
                                {t("page.robots.lifecycle.active")}
                            </FilterChip>
                            <FilterChip id="lifecycle:suspended">
                                {t("page.robots.lifecycle.suspended")}
                            </FilterChip>
                            <FilterChip id="lifecycle:maintenance">
                                {t("page.robots.lifecycle.maintenance")}
                            </FilterChip>
                            <FilterChip id="lifecycle:decommissioned">
                                {t("page.robots.lifecycle.decommissioned")}
                            </FilterChip>
                        </FilterSection>
                    </FilterSelectionContext.Provider>
                    <div className="flex items-center justify-end gap-2">
                        <Button
                            variant="ghost"
                            onClick={() => {
                                onChange(new Set());
                                setOpen(false);
                            }}
                        >
                            {t("page.robots.filterPopup.resetAll")}
                        </Button>
                    </div>
                </PopoverContent>
            </Popover>
        );
    },
);

const categoryLabels: Record<string, string> = {
    quick: "page.robots.filterTags.tag",
    class: "page.robots.filterPopup.class",
    department: "page.robots.details.department",
    type: "page.robots.filterTags.robotType",
    connectivity: "page.robots.column.connectivity",
    health: "page.robots.details.health",
    lifecycle: "page.robots.details.lifecycleState",
};

/**
 * Labels of the static filter chips, by chip id. Must be kept in sync with the
 * chips rendered by the RobotFilterPopover. Department chips are resolved from
 * the repository instead.
 */
const chipLabels: Partial<Record<RobotFilterId, string>> = {
    "quick:needs_attention": "page.robots.filterPopup.needsAttention",
    "quick:close_to_discharge": "page.robots.filterPopup.closeToDischarge",
    "quick:lost": "page.robots.filterPopup.lost",
    "class:agv": "AGV",
    "class:amr": "AMR",
    "type:forklift": "page.robots.type.forklift",
    "type:unit_load": "page.robots.type.unitLoad",
    "type:tugger": "page.robots.type.tugger",
    "type:pallet_mover": "page.robots.type.palletMover",
    "type:cart": "page.robots.type.cart",
    "type:cleaner": "page.robots.type.cleaner",
    "connectivity:online": "page.robots.connectivity.online",
    "connectivity:delayed": "page.robots.connectivity.delayed",
    "connectivity:offline": "page.robots.connectivity.offline",
    "health:healthy": "page.robots.health.healthy",
    "health:warning": "page.robots.health.warning",
    "health:critical": "page.robots.health.critical",
    "lifecycle:planned": "page.robots.lifecycle.planned",
    "lifecycle:commissioning": "page.robots.lifecycle.commissioning",
    "lifecycle:active": "page.robots.lifecycle.active",
    "lifecycle:suspended": "page.robots.lifecycle.suspended",
    "lifecycle:maintenance": "page.robots.lifecycle.maintenance",
    "lifecycle:decommissioned": "page.robots.lifecycle.decommissioned",
};

/**
 * The selected filters as a row of removable tags, with a button clearing
 * all of them at the end. Renders nothing while no filter is selected.
 */
export const RobotFilterTags = observer(
    ({
        selected,
        onChange: onSelectedChange,
        className,
    }: RobotFilterSelectionProps & { className?: string }): JSX.Element => {
        const { t } = useTranslation();

        if (selected.size === 0) {
            return <></>;
        }

        const remove = (id: RobotFilterId): void => {
            const next = new Set(selected);
            next.delete(id);
            onSelectedChange(next);
        };

        const chipLabel = (id: RobotFilterId): string => {
            if (id.startsWith("department:")) {
                const department = store.repositoryDepartments.byId(id.slice("department:".length));
                return department ? store.i18n.formatDepartment(department) : "…";
            }
            return t(chipLabels[id] ?? id);
        };

        return (
            <div className={cn("flex flex-wrap items-center gap-2", className)}>
                {[...selected].map((id) => (
                    <Badge variant="muted" key={id}>
                        {`${t(categoryLabels[id.split(":")[0]])}: ${chipLabel(id)}`}
                        <button
                            type="button"
                            className="text-muted-foreground hover:text-foreground cursor-pointer"
                            onClick={() => remove(id)}
                        >
                            <X className="size-3.5" />
                        </button>
                    </Badge>
                ))}
                <button
                    className="text-foreground hover:bg-muted cursor-pointer rounded-md border-none bg-none px-2 py-1"
                    onClick={() => onSelectedChange(new Set())}
                >
                    {t("page.robots.filterTags.clear")}
                </button>
            </div>
        );
    },
);
